import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Send, TestTube2, Mail, ShieldCheck, RefreshCw, ClipboardList, CheckCircle2, XCircle, Users as UsersIcon, ChevronDown, ChevronRight, UserPlus, UserMinus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TEAMS = ["Maquila", "ESI", "CEDIC", "Compras", "Otro"];

/** Row inside RecipientsPanel showing a user + include/exclude toggles. */
function RecipientRow({ user, enabled, onToggleInclude, onToggleExclude, ...rest }) {
  const displayName = user.full_name || user.username;
  const emailMissing = !user.email;
  return (
    <div
      className={`flex items-center gap-2 text-xs py-1 pl-2 rounded ${user.will_receive ? "bg-emerald-500/5" : ""}`}
      {...rest}
    >
      <div className="flex-1 min-w-0">
        <div className="truncate">
          <span className="font-medium text-foreground">{displayName}</span>
          <span className="text-muted-foreground ml-1">@{user.username}</span>
        </div>
        <div className="text-[10px] text-muted-foreground truncate">
          {emailMissing ? <span className="text-amber-400">Sin correo</span> : user.email}
          {user.equipo && user.equipo !== "Otro" && <span> · {user.equipo}</span>}
          {" · "}
          <span className="italic">{user.reason}</span>
        </div>
      </div>
      {!emailMissing && enabled && user.role !== "admin" && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggleInclude}
            title={user.included_individually ? "Quitar inclusión individual" : "Incluir individualmente"}
            className={`h-6 w-6 flex items-center justify-center rounded border transition-colors ${
              user.included_individually
                ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/40"
                : "text-muted-foreground border-border hover:text-emerald-400 hover:border-emerald-500/40"
            }`}
          >
            <UserPlus className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={onToggleExclude}
            title={user.excluded ? "Quitar exclusión" : "Excluir de esta notificación"}
            className={`h-6 w-6 flex items-center justify-center rounded border transition-colors ${
              user.excluded
                ? "text-destructive bg-destructive/10 border-destructive/40"
                : "text-muted-foreground border-border hover:text-destructive hover:border-destructive/40"
            }`}
          >
            <UserMinus className="h-3 w-3" />
          </button>
        </div>
      )}
      {user.role === "admin" && !emailMissing && (
        <span className="text-[9px] text-primary shrink-0 font-mono uppercase">ADMIN</span>
      )}
    </div>
  );
}

/**
 * Admin-only page to configure per-event email notifications sent via Resend.
 * Everything is dynamic: events, teams and extras.
 */
export default function NotificationsPage() {
  const qc = useQueryClient();

  const { data: settings = [], isLoading, refetch } = useQuery({
    queryKey: ["notification-settings"],
    queryFn: async () => (await api.get("/notifications/settings")).data,
  });

  const { data: log = [] } = useQuery({
    queryKey: ["notification-log"],
    queryFn: async () => (await api.get("/notifications/log", { params: { limit: 30 } })).data,
    staleTime: 15_000,
  });

  const [savingKey, setSavingKey] = useState(null);
  const [extrasDraft, setExtrasDraft] = useState({}); // per event key
  const [testTarget, setTestTarget] = useState(null); // { event_key, label }
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [expanded, setExpanded] = useState({}); // { event_key: true }

  const RecipientsPanel = ({ eventKey, currentSettings }) => {
    const { data, isLoading: recLoading, refetch: refetchRec } = useQuery({
      queryKey: ["notification-recipients", eventKey],
      queryFn: async () => (await api.get(`/notifications/recipients/${eventKey}`)).data,
      staleTime: 10_000,
      enabled: expanded[eventKey] === true,
    });
    const users = data?.users || [];
    const extras = data?.extra_emails || [];

    const toggleInclude = async (uid, currentlyIncluded) => {
      const next = currentlyIncluded
        ? (currentSettings.include_user_ids || []).filter((x) => x !== uid)
        : [...(currentSettings.include_user_ids || []), uid];
      // Also make sure we don't exclude what we're including
      const nextExclude = (currentSettings.exclude_user_ids || []).filter((x) => x !== uid);
      await patch(eventKey, { include_user_ids: next, exclude_user_ids: nextExclude });
      refetchRec();
    };

    const toggleExclude = async (uid, currentlyExcluded) => {
      const next = currentlyExcluded
        ? (currentSettings.exclude_user_ids || []).filter((x) => x !== uid)
        : [...(currentSettings.exclude_user_ids || []), uid];
      // Also drop from include if we're excluding
      const nextInclude = (currentSettings.include_user_ids || []).filter((x) => x !== uid);
      await patch(eventKey, { exclude_user_ids: next, include_user_ids: nextInclude });
      refetchRec();
    };

    if (recLoading) return <div className="text-xs text-muted-foreground py-3">Cargando destinatarios...</div>;

    const receiving = users.filter((u) => u.will_receive);
    const notReceiving = users.filter((u) => !u.will_receive);

    return (
      <div className="border-t border-border pt-3 mt-1 space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 text-emerald-400" /> Reciben este evento hoy ({receiving.length + extras.length})
          </div>
          {receiving.length === 0 && extras.length === 0 ? (
            <div className="text-xs text-muted-foreground italic pl-3">Nadie recibiría este correo con la configuración actual.</div>
          ) : (
            <div className="space-y-1 max-h-56 overflow-auto pr-1">
              {receiving.map((u) => (
                <RecipientRow
                  key={u.id}
                  user={u}
                  enabled={currentSettings.enabled}
                  onToggleInclude={() => toggleInclude(u.id, u.included_individually)}
                  onToggleExclude={() => toggleExclude(u.id, u.excluded)}
                  data-testid={`recipient-${eventKey}-${u.username}`}
                />
              ))}
              {extras.map((em) => (
                <div key={em} className="flex items-center gap-2 text-xs py-1 pl-2">
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  <span className="font-mono flex-1 truncate" title={em}>{em}</span>
                  <span className="text-[10px] text-muted-foreground italic">correo extra</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {notReceiving.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
              <XCircle className="h-3 w-3 text-muted-foreground" /> No reciben ({notReceiving.length})
            </div>
            <div className="space-y-1 max-h-40 overflow-auto pr-1">
              {notReceiving.map((u) => (
                <RecipientRow
                  key={u.id}
                  user={u}
                  enabled={currentSettings.enabled}
                  onToggleInclude={() => toggleInclude(u.id, u.included_individually)}
                  onToggleExclude={() => toggleExclude(u.id, u.excluded)}
                  data-testid={`recipient-${eventKey}-${u.username}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const patch = async (event_key, body) => {
    setSavingKey(event_key);
    try {
      await api.patch(`/notifications/settings/${event_key}`, body);
      await refetch();
      qc.invalidateQueries({ queryKey: ["notification-settings"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingKey(null);
    }
  };

  const toggleEnabled = (s, next) => patch(s.event_key, { enabled: next });

  const toggleTeam = (s, team) => {
    const has = s.teams.includes(team);
    const next = has ? s.teams.filter((t) => t !== team) : [...s.teams, team];
    patch(s.event_key, { teams: next });
  };

  const saveExtras = (s) => {
    const draft = (extrasDraft[s.event_key] ?? s.extra_emails.join(", "));
    const list = draft.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    patch(s.event_key, { extra_emails: list }).then(() => {
      toast.success("Correos extra guardados");
      setExtrasDraft((d) => ({ ...d, [s.event_key]: undefined }));
    });
  };

  const sendTest = async () => {
    if (!testTarget || !testEmail.trim()) return;
    setSendingTest(true);
    try {
      const res = await api.post("/notifications/test", {
        event_key: testTarget.event_key,
        recipient: testEmail.trim(),
      });
      toast.success(`Correo de prueba enviado. ID Resend: ${res.data?.resend_id || "(ok)"}`);
      setTestTarget(null);
      setTestEmail("");
      qc.invalidateQueries({ queryKey: ["notification-log"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falló el envío de prueba");
    } finally {
      setSendingTest(false);
    }
  };

  const enabledCount = useMemo(() => settings.filter((s) => s.enabled).length, [settings]);

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">Notificaciones por correo</h1>
          <p className="text-sm text-muted-foreground">
            Activa o desactiva cada evento y define qué equipos reciben el correo. Los administradores con correo configurado siempre reciben todos los eventos activos.
          </p>
        </div>
        <Badge variant="outline" className="gap-1">
          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          {enabledCount} de {settings.length} eventos activos
        </Badge>
        <Button variant="outline" size="sm" onClick={refetch} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      <Card className="bg-primary/5 border-primary/30">
        <CardContent className="p-3 flex items-start gap-2 text-xs">
          <ShieldCheck className="h-4 w-4 text-primary mt-0.5" />
          <div className="text-muted-foreground">
            Los destinatarios se resuelven por <span className="font-mono text-foreground">equipo</span> del usuario más los correos extra listados. Asigna el equipo desde <span className="font-mono text-foreground">Usuarios → Equipo/Correo</span>. Si un usuario no tiene correo, no recibe nada aunque sea admin.
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {settings.map((s) => (
          <Card key={s.event_key} className="bg-card border-border" data-testid={`notif-card-${s.event_key}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    {s.label}
                    <span className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">
                      {s.event_key}
                    </span>
                  </CardTitle>
                  <CardDescription className="text-xs">{s.description}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{s.enabled ? "Activo" : "Inactivo"}</span>
                  <Switch
                    checked={s.enabled}
                    onCheckedChange={(v) => toggleEnabled(s, v)}
                    disabled={savingKey === s.event_key}
                    data-testid={`notif-enabled-${s.event_key}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTestTarget(s)}
                    data-testid={`notif-test-${s.event_key}`}
                    className="ml-2"
                    title="Enviar correo de prueba"
                  >
                    <TestTube2 className="h-3.5 w-3.5 mr-1" />
                    Probar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                  Equipos que reciben este evento
                </div>
                <div className="flex flex-wrap gap-2">
                  {TEAMS.map((team) => {
                    const active = s.teams.includes(team);
                    return (
                      <button
                        key={team}
                        onClick={() => toggleTeam(s, team)}
                        disabled={!s.enabled || savingKey === s.event_key}
                        data-testid={`notif-team-${s.event_key}-${team}`}
                        className={`text-xs px-3 py-1.5 rounded-md border transition-all font-medium ${
                          active
                            ? "bg-primary/20 text-primary border-primary/50 shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]"
                            : "bg-muted/30 text-muted-foreground border-border hover:border-primary/30"
                        } ${!s.enabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        {team}
                      </button>
                    );
                  })}
                  <span className="text-[10px] text-muted-foreground self-center italic">
                    (Admins con correo reciben siempre)
                  </span>
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Correos extra (separados por coma)
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={extrasDraft[s.event_key] ?? s.extra_emails.join(", ")}
                    onChange={(e) => setExtrasDraft((d) => ({ ...d, [s.event_key]: e.target.value }))}
                    placeholder="externo1@empresa.com, gerencia@empresa.com"
                    rows={2}
                    className="text-xs font-mono flex-1"
                    disabled={!s.enabled}
                    data-testid={`notif-extras-${s.event_key}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => saveExtras(s)}
                    disabled={!s.enabled || savingKey === s.event_key || extrasDraft[s.event_key] === undefined}
                    data-testid={`notif-extras-save-${s.event_key}`}
                  >
                    Guardar
                  </Button>
                </div>
              </div>

              {/* Recipients preview panel (collapsible) */}
              <div>
                <button
                  type="button"
                  onClick={() => setExpanded((x) => ({ ...x, [s.event_key]: !x[s.event_key] }))}
                  className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-primary hover:text-primary/80 transition-colors"
                  data-testid={`notif-recipients-toggle-${s.event_key}`}
                >
                  {expanded[s.event_key] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  <UsersIcon className="h-3 w-3" />
                  Ver destinatarios actuales
                </button>
                {expanded[s.event_key] && (
                  <RecipientsPanel eventKey={s.event_key} currentSettings={s} />
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Log */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" /> Últimos envíos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <div className="text-xs text-muted-foreground py-6 text-center">Sin envíos registrados aún.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead className="text-muted-foreground uppercase text-[10px]">
                  <tr className="border-b border-border">
                    <th className="py-2 px-2 text-left">Fecha</th>
                    <th className="py-2 px-2 text-left">Evento</th>
                    <th className="py-2 px-2 text-left">Asunto</th>
                    <th className="py-2 px-2 text-left">Destinatarios</th>
                    <th className="py-2 px-2 text-left">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((r) => (
                    <tr key={r.id} className="border-b border-border/40" data-testid={`notif-log-${r.id}`}>
                      <td className="py-1.5 px-2 font-mono">{(r.created_at || "").substring(0, 16).replace("T", " ")}</td>
                      <td className="py-1.5 px-2 font-mono">{r.event_key}</td>
                      <td className="py-1.5 px-2 truncate max-w-[280px]" title={r.subject}>{r.subject}</td>
                      <td className="py-1.5 px-2 font-mono text-muted-foreground">
                        {(r.recipients || []).length} correo{(r.recipients || []).length !== 1 ? "s" : ""}
                      </td>
                      <td className="py-1.5 px-2">
                        {r.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Enviado</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-destructive" title={r.error}><XCircle className="h-3 w-3" /> Error</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test send dialog */}
      <Dialog open={!!testTarget} onOpenChange={(o) => { if (!o) { setTestTarget(null); setTestEmail(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><TestTube2 className="h-5 w-5 text-primary" /> Probar "{testTarget?.label}"</DialogTitle>
            <DialogDescription>
              Se enviará un correo de ejemplo con datos ficticios para que veas cómo se ve el mensaje.
              {" "}En modo prueba (sender <span className="font-mono">onboarding@resend.dev</span>) Resend sólo entrega a correos verificados en tu cuenta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Destinatario</label>
            <Input
              type="email"
              placeholder="tu@correo.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              data-testid="notif-test-email"
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTestTarget(null)}>Cancelar</Button>
            <Button onClick={sendTest} disabled={sendingTest || !testEmail.trim()} data-testid="notif-test-send">
              <Send className="h-4 w-4 mr-1" /> {sendingTest ? "Enviando..." : "Enviar prueba"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
